// File: src/api/n8nAdapter.ts
// N8N Chat Adapter - Transforms n8n streaming events to the chat UI protocol

import { ChatAppRequest, ChatAppResponse, toN8nSiteId, SiteId } from "./models";
import { makeNamespace, storageGet, storageSet } from "../utils/storage";
import { getWidgetPersistenceConfig, WIDGET_STORAGE_SCHEMA_VERSION } from "../utils/widgetPersistence";
import type { AnswerClosure, ClosureReason } from "../utils/answerClosure";
import { threadStorageSuffix } from "../utils/conversationThread";

/**
 * Generate UUID v4
 */
function generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replaceAll(/[xy]/g, function (c) {
        const r = Math.trunc(Math.random() * 16);
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// Used when persistence is disabled: conversation continuity is retained only for
// the lifetime of this loaded widget and never written to Web Storage.
const volatileSessions = new Map<string, string>();

function sessionUserId(userId?: string): string {
    return userId || (globalThis as any).PublicWidgetConfig?.userId || "default";
}

/**
 * Namespaced, TTL'd storage key for the n8n session, scoped by page origin + userId + site
 * so different users, sites and origins get independent conversations — and so the session
 * expires on the same schedule as chat history.
 * ENG-054: and by conversation thread. The general thread (threadKey omitted or "general") keeps
 * the key it had before threads existed, so existing conversations continue; a product thread
 * gets `n8n_session_${site}__p:<sku>` (utils/conversationThread.ts threadStorageSuffix).
 */
function n8nSessionKey(siteId?: SiteId, userId?: string, threadKey?: string): string {
    const site = siteId || "PUBLIC_GR";
    const ns = makeNamespace(window.location.origin, sessionUserId(userId), WIDGET_STORAGE_SCHEMA_VERSION);
    return ns.key(`n8n_session_${site}${threadStorageSuffix(threadKey)}`);
}

/**
 * Legacy pre-TTL key (plain string, no expiry). Migrated into TTL storage on first read,
 * then removed, so an in-flight conversation survives the upgrade exactly once.
 */
function legacyN8nSessionKey(siteId?: SiteId, userId?: string): string {
    const site = siteId || "PUBLIC_GR";
    return `n8n_session_id_${site}_${sessionUserId(userId)}`;
}

/**
 * Get or create the n8n session ID. The session shares chat history's TTL: reusing it
 * refreshes the expiry (sliding window), and once it expires a fresh session is minted —
 * so expired storage always starts a new backend conversation.
 * ENG-054: one session per conversation thread (`threadKey`, omitted = the general thread).
 */
export function getN8NSessionId(siteId?: SiteId, userId?: string, threadKey?: string): string {
    const key = n8nSessionKey(siteId, userId, threadKey);
    const persistence = getWidgetPersistenceConfig();

    if (!persistence.enabled) {
        const existing = volatileSessions.get(key);
        if (existing) return existing;
        const sessionId = generateUUID();
        volatileSessions.set(key, sessionId);
        return sessionId;
    }

    let sessionId = storageGet<string>(key);

    // One-time migration of a legacy plain-string session value (general thread only: the
    // legacy key predates threads).
    if (!sessionId && !threadStorageSuffix(threadKey)) {
        try {
            const legacyKey = legacyN8nSessionKey(siteId, userId);
            const legacy = localStorage.getItem(legacyKey);
            if (legacy) {
                sessionId = legacy;
                localStorage.removeItem(legacyKey);
                console.log("🔁 Migrated legacy n8n session into TTL storage:", sessionId);
            }
        } catch {}
    }

    if (sessionId) {
        // Refresh the TTL so an active conversation keeps its session (history is likewise
        // re-persisted every turn).
        storageSet(key, sessionId, {
            schemaVersion: WIDGET_STORAGE_SCHEMA_VERSION,
            ttlMs: persistence.ttlMs
        });
        console.log("♻️ Reusing n8n session:", sessionId, `(site: ${siteId || "PUBLIC_GR"}, thread: ${threadKey || "general"})`);
        return sessionId;
    }

    sessionId = generateUUID();
    storageSet(key, sessionId, {
        schemaVersion: WIDGET_STORAGE_SCHEMA_VERSION,
        ttlMs: persistence.ttlMs
    });
    console.log("🆕 Created new n8n session:", sessionId, `(site: ${siteId || "PUBLIC_GR"}, thread: ${threadKey || "general"})`);
    return sessionId;
}

/**
 * Clear the n8n session (new-conversation reset). Removes the current key and any legacy
 * key so it cannot resurrect.
 * ENG-054: only the given thread's session (`threadKey`, omitted = the general thread).
 */
export function clearN8NSession(siteId?: SiteId, userId?: string, threadKey?: string): void {
    const key = n8nSessionKey(siteId, userId, threadKey);
    volatileSessions.delete(key);
    try {
        localStorage.removeItem(key);
    } catch {}
    if (!threadStorageSuffix(threadKey)) {
        try {
            localStorage.removeItem(legacyN8nSessionKey(siteId, userId));
        } catch {}
    }
    console.log("🧹 Cleared n8n session", `(site: ${siteId || "PUBLIC_GR"}, thread: ${threadKey || "general"})`);
}

/**
 * Check if n8n is enabled
 */
export function isN8NEnabled(): boolean {
    const n8nUrl = (globalThis as any).N8N_CHAT_URL || localStorage.getItem("N8N_CHAT_URL");
    return !!n8nUrl;
}

/**
 * Get n8n webhook URL
 */
export function getN8NUrl(): string | null {
    return (globalThis as any).N8N_CHAT_URL || localStorage.getItem("N8N_CHAT_URL");
}

/**
 * Set n8n webhook URL
 */
export function setN8NUrl(url: string | null): void {
    if (url) {
        localStorage.setItem("N8N_CHAT_URL", url);
        console.log("✅ n8n enabled:", url);
    } else {
        localStorage.removeItem("N8N_CHAT_URL");
        console.log("❌ n8n disabled");
    }
}

type MetadataBlock = {
    type?: string;
    message?: string;
    emoji?: string;
};

export type WebCitationItem = {
    source?: string;
    url?: string;
    claim?: string;
};

type CitationsBlock = {
    type?: string;
    items?: WebCitationItem[];
};

type StreamState = {
    contentBuffer: string;
    guardrailBuffer: string;
    /** "idle" = waiting for a type header, "streaming" = passing content through, "citations-capture" = accumulating a trailing citations JSON block */
    contentMode: "idle" | "streaming" | "citations-capture";
    lastMetadataTime: number;
    shouldCloseStream: boolean;
    lastEventTime: number;
    hasReceivedContent: boolean;
    streamStartTime: number;
    /** Tail of the last streamed chunk held back to detect a citations marker that spans chunk boundaries. */
    streamingTailBuffer: string;
    /** Accumulator for a captured `{"type": "citations"}` JSON block. */
    citationsBuffer: string;
};

function countUnclosedBraces(text: string): number {
    let braceCount = 0;
    let inString = false;
    let escapeNext = false;

    for (const char of text) {
        if (escapeNext) {
            escapeNext = false;
            continue;
        }
        if (char === "\\") {
            escapeNext = true;
            continue;
        }
        if (char === '"') {
            inString = !inString;
            continue;
        }
        if (!inString) {
            if (char === "{") braceCount++;
            if (char === "}") braceCount--;
        }
    }

    return braceCount;
}

/** Valid type headers from the agent protocol */
const VALID_CONTENT_TYPES = new Set(["markdown", "structured"]);

/** Pattern that signals a new top-level JSON line beginning in the stream — used to detect a trailing citations block. */
const NEW_JSON_LINE_REGEX = /\n\s*\{/;
/** Only hold a tiny trailing line boundary so citations markers can span chunks without delaying normal content. */
const CITATIONS_TRAILING_BOUNDARY_REGEX = /\r$|\r?\n[ \t]{0,8}$/;
/** Safety cap on the held-while-classifying buffer. If we can't decide typed vs non-typed
 *  after this many characters, give up and emit as regular content. */
const HOLD_DECISION_CAP = 256;
const AZURE_GUARDRAIL_UI_MESSAGE =
    "This request was blocked by Azure content safety. It may include sexual content, violence, self-harm, hate, or other restricted content.";
const AZURE_GUARDRAIL_PHRASES = [
    "im sorry but i cannot assist with that request",
    "im sorry but i cant assist with that request",
    "im sorry but i cannot help with that request",
    "im sorry but i cant help with that request",
    "i cannot assist with that request",
    "i cant assist with that request",
    "i cannot help with that request",
    "i cant help with that request"
];

function markContentDelivered(state: StreamState): void {
    state.hasReceivedContent = true;
}

function normalizeGuardrailText(text: string): string {
    return text
        .toLowerCase()
        .replaceAll("’", "'")
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function isAzureGuardrailMessage(text: string): boolean {
    const normalized = normalizeGuardrailText(text);
    if (!normalized) return false;

    return (
        AZURE_GUARDRAIL_PHRASES.some(phrase => normalized.includes(phrase)) ||
        normalized.includes("blocked by azure content safety") ||
        normalized.includes("flagged by the openai content filter") ||
        normalized.includes("content was filtered")
    );
}

function isPotentialAzureGuardrailPrefix(text: string): boolean {
    const normalized = normalizeGuardrailText(text);
    if (!normalized) return false;
    return AZURE_GUARDRAIL_PHRASES.some(phrase => phrase.startsWith(normalized));
}

function markAsAzureGuardrailError(n8nEvent: Record<string, unknown>, state: StreamState): void {
    n8nEvent.type = "error";
    n8nEvent.message = AZURE_GUARDRAIL_UI_MESSAGE;
    n8nEvent.content = AZURE_GUARDRAIL_UI_MESSAGE;
    state.contentBuffer = "";
    state.guardrailBuffer = "";
    state.shouldCloseStream = true;
}

function handleAzureGuardrailChunk(n8nEvent: Record<string, unknown>, state: StreamState): "emit-error" | "buffer-more" | "continue" {
    if (n8nEvent.type !== "item") return "continue";

    const chunk = typeof n8nEvent.content === "string" ? n8nEvent.content : "";
    if (!chunk && !state.guardrailBuffer) return "continue";

    const combined = `${state.guardrailBuffer}${chunk}`;
    if (isAzureGuardrailMessage(combined)) {
        console.warn("🛡️ Azure guardrail block detected in n8n stream");
        markAsAzureGuardrailError(n8nEvent, state);
        return "emit-error";
    }

    if (isPotentialAzureGuardrailPrefix(combined)) {
        state.guardrailBuffer = combined;
        return "buffer-more";
    }

    if (state.guardrailBuffer) {
        n8nEvent.content = `${state.guardrailBuffer}${chunk}`;
        state.guardrailBuffer = "";
    }

    return "continue";
}

function emitMetadataEvent(metadata: MetadataBlock, controller: ReadableStreamDefaultController): void {
    if (metadata.emoji || metadata.message) {
        const metadataEvent = {
            choices: [
                {
                    delta: {
                        emoji: metadata.emoji,
                        message: metadata.message,
                        isMetadata: true
                    },
                    index: 0
                }
            ]
        };
        const encoded = new TextEncoder().encode(JSON.stringify(metadataEvent) + "\n");
        controller.enqueue(encoded);
    }
}

function emitContentEvent(content: string, controller: ReadableStreamDefaultController, state: StreamState): void {
    if (!content) return;
    const event = {
        choices: [
            {
                delta: { content },
                index: 0
            }
        ]
    };
    markContentDelivered(state);
    controller.enqueue(new TextEncoder().encode(JSON.stringify(event) + "\n"));
}

function emitCitationsEvent(items: WebCitationItem[], controller: ReadableStreamDefaultController): void {
    if (!items || items.length === 0) return;
    const sanitized = items
        .filter((it): it is WebCitationItem => !!it && (typeof it.source === "string" || typeof it.url === "string"))
        .map(it => ({
            source: typeof it.source === "string" ? it.source.trim() : "",
            url: typeof it.url === "string" ? it.url.trim() : "",
            claim: typeof it.claim === "string" ? it.claim.trim() : ""
        }))
        .filter(it => it.source || it.url);

    if (sanitized.length === 0) return;

    const event = {
        choices: [
            {
                delta: {
                    isWebCitations: true,
                    items: sanitized
                },
                index: 0
            }
        ]
    };
    const encoded = new TextEncoder().encode(JSON.stringify(event) + "\n");
    controller.enqueue(encoded);
}

/**
 * Returns the first balanced JSON object from the start of `input` if one is complete,
 * along with the remainder. If no complete object is present yet (still streaming),
 * returns null.
 */
function tryExtractFirstJsonObject(input: string): { obj: string; rest: string } | null {
    const { objects } = extractJsonObjects(input);
    if (objects.length === 0) return null;
    const first = objects[0];
    const startIdx = input.indexOf(first);
    if (startIdx < 0) return null;
    const endIdx = startIdx + first.length;
    return { obj: first, rest: input.slice(endIdx) };
}

/**
 * Classify the start of a buffered JSON candidate as either typed (has a
 * `"type":"..."` field as its first key, indicating a citations or similar
 * typed line), non-typed (some other top-level key like `"intro"`), or
 * unknown (too little content yet to decide).
 *
 * The "unknown" outcome is critical: when a `\n{` boundary lands at a chunk
 * edge, we may only have seen `{"` or `{"ty` — not enough to know yet. In
 * that case we must HOLD the content (rather than emit it as content or
 * commit to capture mode), and re-classify after the next chunk arrives.
 */
function classifyJsonStart(candidate: string): "typed" | "non-typed" | "unknown" {
    const s = candidate.replace(/^\s+/, "");
    if (!s.startsWith("{")) return "non-typed";

    // Definitive: full `"type":"..."` field present
    if (/"type"\s*:\s*"/.test(s)) return "typed";

    const afterBrace = s.slice(1).replace(/^\s+/, "");
    if (afterBrace.length === 0) return "unknown"; // just `{`
    if (!afterBrace.startsWith('"')) return "unknown"; // not yet at a key

    // Try to read the first key
    const keyMatch = /^"([^"\\]*)"/.exec(afterBrace);
    if (keyMatch) {
        return keyMatch[1] === "type" ? "typed" : "non-typed";
    }

    // Key still streaming (no closing quote yet): could it still become "type"?
    const partial = afterBrace.slice(1); // chars after opening quote
    if (partial.length === 0) return "unknown";
    if ("type".startsWith(partial) || partial.startsWith("type")) return "unknown";
    return "non-typed";
}

/**
 * Inspect a chunk arriving in streaming mode for a trailing typed JSON line
 * (citations). Returns the content portion to emit (everything before the
 * marker), and mutates state to enter citations-capture mode if the marker
 * is found.
 *
 * Three outcomes when a `\n{` boundary is detected:
 *  - typed → capture mode, emit only the content before the newline
 *  - non-typed → pass everything through (it's some other JSON in the body)
 *  - unknown → HOLD the entire combined buffer (emit nothing) and wait for
 *    more chunks so we can classify correctly. Without this hold, partial
 *    citations chunks like `\n{"` leak into the rendered content stream
 *    and the citations block ends up displayed instead of captured.
 *
 * Safety cap: if the hold grows past HOLD_DECISION_CAP without resolving,
 * emit the held content as regular content and abandon the detection.
 */
function splitStreamingChunkAroundCitations(state: StreamState, chunk: string): string {
    const combined = state.streamingTailBuffer + chunk;
    state.streamingTailBuffer = "";

    const newLineMatch = NEW_JSON_LINE_REGEX.exec(combined);
    if (newLineMatch) {
        const splitAt = newLineMatch.index;
        const before = combined.slice(0, splitAt);
        const fromBrace = combined.slice(splitAt);

        const classification = classifyJsonStart(fromBrace);
        if (classification === "typed") {
            state.contentMode = "citations-capture";
            state.citationsBuffer = fromBrace;
            return before.replace(/\s+$/, "");
        }
        if (classification === "non-typed") {
            // Not a typed JSON marker — pass everything through unchanged
            return combined;
        }
        // Unknown — hold the entire combined buffer until we have enough
        // content to classify. Apply a safety cap so we don't hold forever.
        if (combined.length > HOLD_DECISION_CAP) {
            // Give up on detection: emit as content
            return combined;
        }
        state.streamingTailBuffer = combined;
        return "";
    }

    // No `\n{` boundary — hold only a tiny trailing line boundary in case the
    // following chunk starts with `{`. This avoids delaying normal content.
    const boundaryMatch = combined.match(CITATIONS_TRAILING_BOUNDARY_REGEX);
    if (boundaryMatch) {
        const held = boundaryMatch[0];
        state.streamingTailBuffer = held;
        return combined.slice(0, combined.length - held.length);
    }
    return combined;
}

/**
 * Try to consume a complete JSON object from the citations buffer.
 *
 * Three outcomes once a complete JSON object is available:
 *  - It's a `{type: "citations", items: [...]}` block → emit a citations event,
 *    drop the captured bytes from the rendered content, leave mode as "idle"
 *    (citations is typically the final block of a turn).
 *  - It's some other JSON (e.g. a `{"type": "example"}` inside a markdown code
 *    block) → **restore the captured bytes as a regular content delta** and
 *    switch back to "streaming". This prevents data loss when the streaming
 *    detector eagerly captured something that turned out not to be citations.
 *  - Parse error on a balanced object (rare) → restore as content too. Better
 *    to show malformed text than to vanish it.
 *
 * If the buffer isn't yet a complete JSON object, returns false (keep waiting).
 */
function tryFlushCitationsBuffer(state: StreamState, controller: ReadableStreamDefaultController): boolean {
    const trimmed = state.citationsBuffer.replace(/^\s+/, "");
    const parsed = tryExtractFirstJsonObject(trimmed);
    if (!parsed) return false;

    const restoreAsContent = (reason: string) => {
        console.debug(`ℹ️ Captured JSON is not a citations block (${reason}); restoring as content`);
        emitContentEvent(state.citationsBuffer, controller, state);
        state.citationsBuffer = "";
        state.contentMode = "streaming";
    };

    try {
        const block = JSON.parse(parsed.obj) as CitationsBlock;
        if (block?.type === "citations" && Array.isArray(block.items)) {
            emitCitationsEvent(block.items, controller);
            console.debug(`📎 Emitted ${block.items.length} web citation(s)`);
            state.citationsBuffer = parsed.rest.trim() ? parsed.rest : "";
            state.contentMode = "idle";
            return true;
        }
        restoreAsContent(`type=${block?.type ?? "<missing>"}`);
        return true;
    } catch (e) {
        console.warn("⚠️ Failed to parse balanced JSON in citations buffer:", parsed.obj.slice(0, 80), e);
        restoreAsContent("parse-error");
        return true;
    }
}

/**
 * Handle a content chunk while we're already in streaming pass-through mode.
 * Detects a trailing citations JSON line and switches to capture mode if found.
 */
function handleStreamingChunk(n8nEvent: Record<string, unknown>, state: StreamState, chunk: string): boolean {
    const toEmit = splitStreamingChunkAroundCitations(state, chunk);
    n8nEvent.content = toEmit;
    return toEmit !== "";
}

/**
 * Handle a content chunk while accumulating a citations JSON block. Parsing is
 * intentionally centralized in processN8NLine so restored/citation events are
 * emitted only after any preceding content delta.
 */
function handleCitationsCaptureChunk(state: StreamState, chunk: string): boolean {
    state.citationsBuffer += chunk;
    return false;
}

/**
 * Processes item content using the type-header protocol.
 * In "idle" mode: buffers content looking for JSON type headers.
 * In "streaming" mode: passes content through to the UI.
 * Anything without a valid type header is silently dropped.
 */
async function processItemContent(n8nEvent: Record<string, unknown>, state: StreamState, controller: ReadableStreamDefaultController): Promise<boolean> {
    if (n8nEvent.type !== "item") return true;

    const guardrailAction = handleAzureGuardrailChunk(n8nEvent, state);
    if (guardrailAction === "emit-error") {
        return true;
    }
    if (guardrailAction === "buffer-more") {
        return false;
    }

    const chunk = typeof n8nEvent.content === "string" ? n8nEvent.content : "";
    if (!chunk) return true;

    if (state.contentMode === "streaming") {
        return handleStreamingChunk(n8nEvent, state, chunk);
    }
    if (state.contentMode === "citations-capture") {
        return handleCitationsCaptureChunk(state, chunk);
    }

    // Idle mode: accumulate buffer, look for JSON type headers
    state.contentBuffer += chunk;

    // Strip markdown code fences the model sometimes wraps headers in
    state.contentBuffer = state.contentBuffer.replace(/^```json\s*\n?/, "").replace(/\n?```/, "");
    const trimmed = state.contentBuffer.trim();

    // Not a JSON header — drop (tool call text, junk, etc.)
    if (!trimmed.startsWith("{")) {
        if (isPotentialAzureGuardrailPrefix(trimmed)) return false;
        // Don't drop if buffer might be an incomplete fence (e.g. just "```" or "```j")
        if (state.contentBuffer.startsWith("`")) return false;
        console.log(`🗑️ Dropping non-header content:`, trimmed.substring(0, 80));
        state.contentBuffer = "";
        return false;
    }

    // Might be incomplete JSON — wait for more chunks
    if (countUnclosedBraces(trimmed) > 0) {
        // Check if this is raw structured content (no type header) starting to stream
        // Detect early by looking for known content fields so we don't buffer the entire response
        const RAW_CONTENT_PATTERN = /^\{\s*"(?:intro|products|sentences|review_products|competitor_products)"/;
        if (RAW_CONTENT_PATTERN.test(trimmed)) {
            // Buffer until the intro value is complete (has closing quote) so the
            // AnswerParser can extract and render it cleanly without flashing raw JSON
            const introComplete = /"intro"\s*:\s*"(?:[^"\\]|\\.)*"/.test(trimmed);
            if (!introComplete && /"intro"\s*:\s*"/.test(trimmed)) {
                // Intro started but value still streaming — keep buffering
                return false;
            }
            state.contentMode = "streaming";
            console.debug("📝 Raw structured content detected (intro complete), streaming");
            n8nEvent.content = state.contentBuffer;
            state.contentBuffer = "";
            return true;
        }
        return false;
    }

    // Find the first complete JSON object and everything after it
    const buf = state.contentBuffer;
    state.contentBuffer = "";

    // Walk through the buffer to find the end of the first JSON object
    let depth = 0;
    let inStr = false;
    let esc = false;
    let headerEnd = -1;

    for (let i = 0; i < buf.length; i++) {
        const ch = buf[i];
        if (esc) {
            esc = false;
            continue;
        }
        if (ch === "\\" && inStr) {
            esc = true;
            continue;
        }
        if (ch === '"') {
            inStr = !inStr;
            continue;
        }
        if (inStr) continue;
        if (ch === "{") depth++;
        if (ch === "}") {
            depth--;
            if (depth === 0) {
                headerEnd = i;
                break;
            }
        }
    }

    if (headerEnd === -1) return false;

    const headerStr = buf.slice(0, headerEnd + 1);
    const afterHeader = buf.slice(headerEnd + 1);

    try {
        const header = JSON.parse(headerStr);

        if (header.type === "metadata" && header.message) {
            emitMetadataEvent(header, controller);
            state.lastMetadataTime = Date.now();
            console.debug("📊 Metadata:", header.message);

            // If there's more content after the metadata, put it back in buffer for next call
            if (afterHeader.trim()) {
                state.contentBuffer = afterHeader;
                // Re-process the remaining buffer in this same call
                return processItemContent(n8nEvent, state, controller);
            }
            return false;
        }

        if (VALID_CONTENT_TYPES.has(header.type)) {
            state.contentMode = "streaming";
            console.debug(`📝 Content type: ${header.type}`);

            // Brief delay so metadata status message is visible
            const elapsed = Date.now() - state.lastMetadataTime;
            const minDelay = 1500;
            if (state.lastMetadataTime > 0 && elapsed < minDelay) {
                await new Promise(r => setTimeout(r, minDelay - elapsed));
            }

            // Emit any content that came after the type header in the same chunk
            if (afterHeader.trim()) {
                return handleStreamingChunk(n8nEvent, state, afterHeader);
            }
            return false;
        }

        // No recognized type — check if this is raw content without a header (e.g. structured JSON)
        if (header.intro !== undefined || header.products !== undefined || header.sentences !== undefined || header.review_products !== undefined) {
            state.contentMode = "streaming";
            console.debug("📝 Raw structured content detected (no type header)");
            n8nEvent.content = headerStr + afterHeader;
            return true;
        }

        console.log(`🗑️ Dropping unrecognized header:`, headerStr.substring(0, 80));
        return false;
    } catch {
        console.warn(`⚠️ Failed to parse header:`, headerStr.substring(0, 80));
        return false;
    }
}

/**
 * Called once the n8n stream is done. Flushes anything we were holding back:
 *  - `streamingTailBuffer`: tail of streamed content held while waiting to see
 *    if a citations marker was starting. If the stream ended without one, emit
 *    it as regular content.
 *  - `citationsBuffer`: try one last parse of any captured citations JSON in
 *    case the closing brace arrived right at end-of-stream.
 */
function finalizeStreamState(state: StreamState, controller: ReadableStreamDefaultController): void {
    if (state.streamingTailBuffer) {
        const remaining = state.streamingTailBuffer;
        state.streamingTailBuffer = "";
        emitContentEvent(remaining, controller, state);
    }
    if (state.citationsBuffer) {
        tryFlushCitationsBuffer(state, controller);
        if (state.citationsBuffer) {
            console.warn("⚠️ Stream ended with incomplete citations buffer:", state.citationsBuffer.slice(0, 80));
            state.citationsBuffer = "";
        }
    }
}

function handleFinalBuffer(buffer: string, state: StreamState, controller: ReadableStreamDefaultController): void {
    if (!buffer.trim()) return;

    try {
        const n8nEvent = JSON.parse(buffer);
        if (state.contentMode === "streaming") {
            const transformedEvent = transformN8NEvent(n8nEvent);
            if (transformedEvent) {
                const deltaContent = (transformedEvent as { choices?: Array<{ delta?: { content?: unknown } }> }).choices?.[0]?.delta?.content;
                if (typeof deltaContent === "string" && deltaContent.length > 0) {
                    markContentDelivered(state);
                }
                const encoded = new TextEncoder().encode(JSON.stringify(transformedEvent) + "\n");
                controller.enqueue(encoded);
            }
        }
    } catch (e) {
        console.error("Failed to parse final buffer:", buffer, e);
    }
}

function parseN8NEvent(line: string): Record<string, unknown> | null {
    if (!line.trim()) {
        return null;
    }

    try {
        return JSON.parse(line);
    } catch (e) {
        console.error("Failed to parse n8n event:", line, e);
        return null;
    }
}

type JsonParseState = {
    objects: string[];
    startIndex: number;
    depth: number;
    inString: boolean;
    escape: boolean;
};

function handleStringChar(state: JsonParseState, ch: string): boolean {
    if (!state.inString) {
        return false;
    }

    if (state.escape) {
        state.escape = false;
        return true;
    }

    if (ch === "\\") {
        state.escape = true;
        return true;
    }

    if (ch === '"') {
        state.inString = false;
        return true;
    }

    return true;
}

function handleStructuralChar(state: JsonParseState, buffer: string, ch: string, index: number): void {
    if (ch === '"') {
        state.inString = true;
        return;
    }

    if (ch === "{") {
        if (state.depth === 0) {
            state.startIndex = index;
        }
        state.depth += 1;
        return;
    }

    if (ch !== "}") {
        return;
    }

    if (state.depth > 0) {
        state.depth -= 1;
    }

    if (state.depth === 0 && state.startIndex >= 0) {
        state.objects.push(buffer.slice(state.startIndex, index + 1));
        state.startIndex = -1;
    }
}

function extractJsonObjects(buffer: string): { objects: string[]; remainder: string } {
    const state: JsonParseState = {
        objects: [],
        startIndex: -1,
        depth: 0,
        inString: false,
        escape: false
    };

    for (let i = 0; i < buffer.length; i += 1) {
        const ch = buffer[i];
        if (handleStringChar(state, ch)) {
            continue;
        }
        handleStructuralChar(state, buffer, ch, i);
    }

    const remainder = state.startIndex >= 0 ? buffer.slice(state.startIndex) : "";
    return { objects: state.objects, remainder };
}

function isEndEvent(n8nEvent: Record<string, unknown>): boolean {
    return n8nEvent.type === "end";
}

function extractFallbackContent(n8nEvent: Record<string, unknown>): string {
    const candidateKeys = ["output", "text", "answer", "message", "response"];
    for (const key of candidateKeys) {
        const value = n8nEvent[key];
        if (typeof value === "string" && value.trim()) {
            return value;
        }
    }
    return "";
}

async function enqueueTransformedEvent(
    n8nEvent: Record<string, unknown>,
    state: StreamState,
    controller: ReadableStreamDefaultController,
    artificialDelay: number
): Promise<void> {
    const transformedEvent = transformN8NEvent(n8nEvent);
    if (!transformedEvent) {
        return;
    }

    if (isEndEvent(n8nEvent)) {
        console.debug(`🏁 [${new Date().toISOString()}] ADAPTER: Sending end event to UI:`, transformedEvent);
    }

    if (artificialDelay > 0) {
        await new Promise(resolve => setTimeout(resolve, artificialDelay));
    }

    const deltaContent = (transformedEvent as { choices?: Array<{ delta?: { content?: unknown } }> }).choices?.[0]?.delta?.content;
    if (typeof deltaContent === "string" && deltaContent.length > 0) {
        markContentDelivered(state);
    }

    const encoded = new TextEncoder().encode(JSON.stringify(transformedEvent) + "\n");
    controller.enqueue(encoded);
}

async function processN8NLine(line: string, state: StreamState, controller: ReadableStreamDefaultController, artificialDelay: number): Promise<void> {
    const n8nEvent = parseN8NEvent(line);
    if (!n8nEvent) {
        return;
    }

    state.lastEventTime = Date.now();

    // Reset content buffer on new blocks (begin = new node output)
    if (n8nEvent.type === "begin") {
        state.contentBuffer = "";
        state.streamingTailBuffer = "";
        state.citationsBuffer = "";
        state.contentMode = "idle";
    }

    if (isEndEvent(n8nEvent)) {
        const metadata = n8nEvent.metadata as { nodeName?: string } | undefined;
        console.debug(`🏁 [${new Date().toISOString()}] ADAPTER: end (node: ${metadata?.nodeName || "unknown"})`);
    }

    // Some workflows return a single JSON object instead of item/end stream events
    if (!state.hasReceivedContent) {
        const fallbackContent = extractFallbackContent(n8nEvent);
        if (fallbackContent) {
            state.hasReceivedContent = true;
        }
    }

    // Check for real errors — end the stream immediately
    if (n8nEvent.type === "error") {
        const hasMessage = !!(n8nEvent.content || n8nEvent.message);
        const metadata = n8nEvent.metadata as { nodeName?: string } | undefined;
        if (hasMessage || metadata?.nodeName) {
            console.log(`❌ ADAPTER: Error event, closing stream`);
            state.shouldCloseStream = true;
        }
    }

    const shouldEmit = await processItemContent(n8nEvent, state, controller);

    if (isEndEvent(n8nEvent)) {
        finalizeStreamState(state, controller);
    }

    if (shouldEmit) {
        await enqueueTransformedEvent(n8nEvent, state, controller, artificialDelay);
    }

    if (state.citationsBuffer) {
        tryFlushCitationsBuffer(state, controller);
    }
}

/**
 * Transforms an n8n streaming response to the chat UI protocol
 * Maps n8n {type, content, metadata} to {choices: [{delta: {content}}]}
 *
 * @param artificialDelay - Optional delay in ms between chunks to create smoother streaming effect
 */
export function createN8NStreamAdapter(n8nStream: ReadableStream, artificialDelay: number = 0): ReadableStream {
    const reader = n8nStream.getReader();
    const decoder = new TextDecoder();

    return new ReadableStream({
        async start(controller) {
            let buffer = "";
            const startTime = Date.now();
            const state: StreamState = {
                contentBuffer: "",
                guardrailBuffer: "",
                contentMode: "idle",
                lastMetadataTime: 0,
                shouldCloseStream: false,
                lastEventTime: startTime,
                hasReceivedContent: false,
                streamStartTime: startTime,
                streamingTailBuffer: "",
                citationsBuffer: ""
            };

            // Content timeout: if the upstream goes silent (no events at all) for this long
            // *before any real content arrives*, surface an error. Bumped from 90s → 180s so
            // slow-but-alive searches get the reassurance messages below instead of an error.
            // Kept under the gateway's 250s upstream cap (routers/chat.py).
            const CONTENT_TIMEOUT = 180000;
            let contentTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

            // Reassurance messages shown while the response is still pending (no content yet).
            // These are injected on a wall-clock schedule and reuse the metadata channel, so the
            // UI renders them with its usual emoji + fade behaviour. They deliberately do NOT
            // touch state.lastEventTime, so they never mask a genuinely stalled upstream.
            // `after` = delay since the previous message; once the list is exhausted the last
            // message repeats every REASSURANCE_REPEAT until content arrives or CONTENT_TIMEOUT.
            const REASSURANCE_STEPS: Array<{ after: number; emoji: string; message: string }> = [
                { after: 8000, emoji: "🔎", message: "Searching for the best results" },
                { after: 12000, emoji: "🧠", message: "Analyzing the results" },
                { after: 20000, emoji: "📚", message: "Digging a little deeper for you" },
                { after: 30000, emoji: "⏳", message: "Almost there — thanks for your patience" }
            ];
            const REASSURANCE_REPEAT = 30000;
            let reassuranceTimer: ReturnType<typeof setTimeout> | null = null;
            let reassuranceStep = 0;

            const clearReassurance = () => {
                if (reassuranceTimer) {
                    clearTimeout(reassuranceTimer);
                    reassuranceTimer = null;
                }
            };

            const emitReassurance = () => {
                // Content may have landed between scheduling and firing — never talk over it.
                if (state.hasReceivedContent) {
                    clearReassurance();
                    return;
                }
                const step = REASSURANCE_STEPS[Math.min(reassuranceStep, REASSURANCE_STEPS.length - 1)];
                emitMetadataEvent({ emoji: step.emoji, message: step.message }, controller);
                reassuranceStep++;
                const nextGap = reassuranceStep < REASSURANCE_STEPS.length ? REASSURANCE_STEPS[reassuranceStep].after : REASSURANCE_REPEAT;
                reassuranceTimer = setTimeout(emitReassurance, nextGap);
            };

            // Any activity from the backend (real content, real metadata, even keep-alive bytes)
            // restarts the reassurance schedule from the top, so our canned messages only ever
            // fill a genuine gap of silence and never talk over real events.
            const resetReassurance = () => {
                if (state.hasReceivedContent) return;
                clearReassurance();
                reassuranceStep = 0;
                reassuranceTimer = setTimeout(emitReassurance, REASSURANCE_STEPS[0].after);
            };

            const checkContentTimeout = () => {
                const timeSinceLastEvent = Date.now() - state.lastEventTime;
                if (!state.hasReceivedContent && timeSinceLastEvent >= CONTENT_TIMEOUT) {
                    console.error(
                        `⏱️ [${new Date().toISOString()}] ADAPTER: No content received after ${timeSinceLastEvent}ms of inactivity, sending timeout error`
                    );

                    clearReassurance();

                    // Send error event to frontend
                    const errorEvent = {
                        error: "No response received. The request may have timed out or failed. Please try again."
                    };
                    const encoded = new TextEncoder().encode(JSON.stringify(errorEvent) + "\n");
                    controller.enqueue(encoded);

                    controller.close();
                    reader.cancel();
                }
            };

            // Start content timeout + reassurance timers
            contentTimeoutTimer = setTimeout(checkContentTimeout, CONTENT_TIMEOUT);
            reassuranceTimer = setTimeout(emitReassurance, REASSURANCE_STEPS[0].after);

            try {
                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        if (contentTimeoutTimer) clearTimeout(contentTimeoutTimer);
                        clearReassurance();
                        const finalSplit = extractJsonObjects(buffer);
                        for (const line of finalSplit.objects) {
                            await processN8NLine(line, state, controller, artificialDelay);
                        }
                        if (finalSplit.remainder.trim()) {
                            handleFinalBuffer(finalSplit.remainder, state, controller);
                        }
                        finalizeStreamState(state, controller);

                        // If stream closed but no content was received, send error
                        if (!state.hasReceivedContent) {
                            console.error(`⚠️ [${new Date().toISOString()}] ADAPTER: Stream closed without receiving any content`);
                            const errorEvent = {
                                error: "No response received. The request may have timed out or failed. Please try again."
                            };
                            const encoded = new TextEncoder().encode(JSON.stringify(errorEvent) + "\n");
                            controller.enqueue(encoded);
                        }

                        console.debug(`🏁 [${new Date().toISOString()}] ADAPTER: Stream done, closing controller`);
                        controller.close();
                        break;
                    }

                    // Decode chunk and add to buffer
                    const chunk = decoder.decode(value, { stream: true });

                    buffer += chunk;

                    // Process complete JSON objects as they arrive (newline-delimited or concatenated)
                    const split = extractJsonObjects(buffer);
                    buffer = split.remainder;

                    for (const line of split.objects) {
                        await processN8NLine(line, state, controller, artificialDelay);

                        // If we received content, cancel the pending timers permanently
                        if (state.hasReceivedContent && (contentTimeoutTimer || reassuranceTimer)) {
                            if (contentTimeoutTimer) {
                                clearTimeout(contentTimeoutTimer);
                                contentTimeoutTimer = null;
                            }
                            clearReassurance();
                            console.debug(`✅ [${new Date().toISOString()}] ADAPTER: Content received, cancelled pending timers`);
                        }

                        // If we received a real error, close the stream immediately
                        if (state.shouldCloseStream) {
                            if (contentTimeoutTimer) clearTimeout(contentTimeoutTimer);
                            clearReassurance();
                            console.log(`❌ [${new Date().toISOString()}] ADAPTER: Error event received, closing stream immediately`);
                            controller.close();
                            await reader.cancel();
                            return;
                        }
                    }

                    // A chunk arrived from the backend — treat any activity as "still alive".
                    // Push back both the hard content-timeout and the reassurance schedule so
                    // canned messages only ever appear during a real gap of silence.
                    if (!state.hasReceivedContent) {
                        if (contentTimeoutTimer) {
                            clearTimeout(contentTimeoutTimer);
                            contentTimeoutTimer = setTimeout(checkContentTimeout, CONTENT_TIMEOUT);
                        }
                        resetReassurance();
                    }
                }
            } catch (error) {
                if (contentTimeoutTimer) clearTimeout(contentTimeoutTimer);
                clearReassurance();
                console.error("❌ Stream error:", error);
                controller.error(error);
            }
        }
    });
}

const CLOSURE_REASONS: readonly ClosureReason[] = ["answered", "no_results", "clarifying", "error"];

function parseEndClosure(value: unknown): AnswerClosure | undefined {
    if (!value || typeof value !== "object") return undefined;
    const { closes, reason } = value as { closes?: unknown; reason?: unknown };
    if (typeof closes !== "boolean" || !CLOSURE_REASONS.includes(reason as ClosureReason)) return undefined;
    return { closes, reason: reason as ClosureReason };
}

/**
 * Transforms a single n8n event to the chat UI protocol
 */
function transformN8NEvent(n8nEvent: Record<string, unknown>): Record<string, unknown> | null {
    if (typeof n8nEvent.type !== "string") {
        const fallbackContent = extractFallbackContent(n8nEvent);
        if (fallbackContent) {
            if (isAzureGuardrailMessage(fallbackContent)) {
                return {
                    error: AZURE_GUARDRAIL_UI_MESSAGE
                };
            }
            return {
                choices: [
                    {
                        delta: {
                            content: fallbackContent
                        },
                        index: 0
                    }
                ]
            };
        }
    }

    switch (n8nEvent.type) {
        case "begin":
            // Log node start but don't send to frontend
            // console.log('🎬 n8n node begin:', n8nEvent.metadata);
            return null;

        case "item":
            if (typeof n8nEvent.content === "string" && isAzureGuardrailMessage(n8nEvent.content)) {
                return {
                    error: AZURE_GUARDRAIL_UI_MESSAGE
                };
            }
            // Map the content chunk to the UI's delta format
            return {
                choices: [
                    {
                        delta: {
                            content: n8nEvent.content
                        },
                        index: 0
                    }
                ]
            };

        case "end": {
            // Log completion and send context to signal stream end
            const metadata = n8nEvent.metadata as { nodeName?: string } | undefined;
            const nodeName = metadata?.nodeName || "unknown";
            console.debug(`🏁 [${new Date().toISOString()}] ADAPTER: Received end event from n8n (node: ${nodeName})`);
            // ENG-027: the agent backend says whether the answer closes the question; n8n sends
            // no closure, so context.closure stays undefined and the chat classifies the text.
            const closure = parseEndClosure(n8nEvent.closure);
            return {
                choices: [
                    {
                        context: {
                            data_points: [],
                            followup_questions: [],
                            thoughts: [],
                            ...(closure ? { closure } : {})
                        },
                        index: 0
                    }
                ]
            };
        }

        case "error": {
            // Map error to Python error format
            let errorMessage = n8nEvent.content || n8nEvent.message;

            // If no explicit error message, try to construct one from metadata
            if (!errorMessage) {
                const metadata = n8nEvent.metadata as { nodeName?: string } | undefined;
                if (metadata?.nodeName) {
                    errorMessage = `Error occurred in workflow node: ${metadata.nodeName}`;
                    console.error("❌ n8n error event (no message, using node name):", n8nEvent);
                } else {
                    // Truly empty error - ignore it
                    console.warn("⚠️ n8n sent empty error event (ignoring):", n8nEvent);
                    return null;
                }
            }

            console.error("❌ n8n error event:", n8nEvent);
            console.error("❌ n8n error message:", errorMessage);
            return {
                error: typeof errorMessage === "string" && isAzureGuardrailMessage(errorMessage) ? AZURE_GUARDRAIL_UI_MESSAGE : errorMessage
            };
        }

        default:
            console.warn("⚠️ Unknown n8n event type:", n8nEvent.type, n8nEvent);
            // Don't crash on unknown events - just ignore them
            return null;
    }
}

/**
 * Send chat request to n8n webhook
 */
export async function chatWithN8N(request: ChatAppRequest): Promise<Response> {
    const n8nUrl = getN8NUrl();
    if (!n8nUrl) {
        throw new Error("n8n URL not configured");
    }

    // Get persistent session ID (UUID format)
    // Extract last message (n8n has memory, doesn't need full history)
    const lastMessage = request.messages.at(-1)?.content || "";

    // Extract price_source and siteId from request context
    const priceSource = request.context?.overrides?.price_source || "web";
    const siteId: SiteId = request.context?.overrides?.site_id || "PUBLIC_GR";
    // ENG-054: the request already carries its conversation thread's session.
    const sessionId = request.session_state?.n8n_session_id || getN8NSessionId(siteId);
    const n8nSiteId = toN8nSiteId(siteId);

    // Build n8n request matching their expected format
    const n8nRequest = {
        action: "sendMessage",
        sessionId: sessionId,
        chatInput: lastMessage,
        priceSource: priceSource,
        siteId: n8nSiteId
    };

    console.log("📤 Sending to n8n:", {
        sessionId,
        message: lastMessage.substring(0, 50) + "...",
        priceSource,
        siteId: n8nSiteId,
        url: n8nUrl
    });

    const n8nResponse = await fetch(n8nUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
        },
        body: JSON.stringify(n8nRequest)
    });

    if (!n8nResponse.ok) {
        throw new Error(`n8n request failed: ${n8nResponse.status} ${n8nResponse.statusText}`);
    }

    // Handle streaming vs non-streaming
    if (request.stream && n8nResponse.body) {
        console.log("🌊 n8n streaming response");

        // Transform the n8n stream to the chat UI protocol
        // artificialDelay: 0 = no delay (default), 10-30ms = smooth typewriter effect
        // You can adjust this value to control streaming smoothness
        const ARTIFICIAL_DELAY_MS = 0; // Temporarily set to 0 to debug
        const transformedStream = createN8NStreamAdapter(n8nResponse.body, ARTIFICIAL_DELAY_MS);

        return new Response(transformedStream, {
            status: 200,
            headers: {
                "Content-Type": "application/json-lines",
                "X-Powered-By": "n8n",
                "X-Session-Id": sessionId
            }
        });
    } else {
        console.log("📦 n8n non-streaming response");

        // Non-streaming response
        const n8nData = await n8nResponse.json();
        const transformedResponse: ChatAppResponse = {
            choices: [
                {
                    index: 0,
                    message: {
                        content: n8nData.output || n8nData.text || "",
                        role: "assistant"
                    },
                    context: {
                        data_points: [],
                        followup_questions: [],
                        thoughts: []
                    },
                    session_state: {
                        n8n_session_id: sessionId
                    }
                }
            ]
        };

        return new Response(JSON.stringify(transformedResponse), {
            status: n8nResponse.status,
            headers: {
                "Content-Type": "application/json",
                "X-Session-Id": sessionId,
                "X-Powered-By": "n8n"
            }
        });
    }
}
